import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spin, message } from "antd";
import { ratioToStockPricePct } from "fin-web-components";
import { useSearchParams } from "react-router-dom";
import { H5SubPageShell } from "../../components/common/H5SubPageShell";
import "../../components/common/H5SubPageShell.css";
import StockDetailModal from "../stock/StockDetailModal";
import { useMainScrollPullRefresh } from "../../utils/useMainScrollPullRefresh";
import { ConceptApi } from "./ConceptApi";
import { CONCEPT_METRIC_COLUMNS, conceptPageCssVars } from "./Config";
import {
  formatMarketCapValue,
  formatConceptStockCodeDisplay,
  getMetricColumnHeaderLabel,
} from "./ConceptFormat";
import ConceptSearchSheet from "./ConceptSearchSheet";
import ConceptSelectedBar from "./ConceptSelectedBar";
import ConceptUsageGuide from "./ConceptUsageGuide";
import { sortConceptStockRows } from "./ConceptSort";
import type {
  ConceptBrief,
  ConceptMetricField,
  ConceptSortField,
  ConceptSortMode,
  ConceptSortOrder,
  ConceptStockRow,
} from "./ConceptType";
import "./ConceptPage.css";

const CONCEPT_PAGE_SCROLL_SELECTOR = ".lf-concept-page-shell .h5-sub-page__body";

export interface ConceptPageProps {
  spaceId: string;
  spaceProductId: string;
  displayName: string;
}

function formatPeRatio(v: number): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

function formatDividendYield(v: number): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(2)}%`;
}

function formatScore(v: number): string {
  if (!Number.isFinite(v)) return "—";
  return v.toFixed(1);
}

function formatMetricValue(row: ConceptStockRow, field: ConceptMetricField): string {
  switch (field) {
    case "fundamentalScore":
    case "positionScore":
      return formatScore(row[field]);
    case "marketValue":
    case "floatMarketValue":
      return formatMarketCapValue(row[field]);
    case "peRatio":
      return formatPeRatio(row.peRatio);
    case "dividendYield":
      return formatDividendYield(row.dividendYield);
    default:
      return "—";
  }
}

function priceToneClass(pctChange: number): string {
  if (!Number.isFinite(pctChange) || pctChange === 0) {
    return "lf-concept-page__num--flat";
  }
  return pctChange > 0 ? "lf-concept-page__num--up" : "lf-concept-page__num--down";
}

function formatPctDisplay(pctChange: number): string {
  if (!Number.isFinite(pctChange)) {
    return "—";
  }
  const pct = ratioToStockPricePct(pctChange);
  if (!Number.isFinite(pct)) {
    return "—";
  }
  return `${pct > 0 ? "+" : ""}${pct.toFixed(2)}%`;
}

interface SortHeaderProps {
  label: string;
  field: ConceptSortField;
  active: boolean;
  order: ConceptSortOrder;
  onToggle: (field: ConceptSortField) => void;
}

function SortHeader(props: SortHeaderProps): JSX.Element {
  const { label, field, active, order, onToggle } = props;
  return (
    <button type="button" className="lf-concept-page__sort-th" onClick={() => onToggle(field)}>
      <span className="lf-concept-page__sort-label">{label}</span>
      <span className="lf-concept-page__sort-carets" aria-hidden>
        <span
          className={
            active && order === "asc"
              ? "lf-concept-page__caret lf-concept-page__caret--active"
              : "lf-concept-page__caret"
          }
        >
          ▲
        </span>
        <span
          className={
            active && order === "desc"
              ? "lf-concept-page__caret lf-concept-page__caret--active"
              : "lf-concept-page__caret"
          }
        >
          ▼
        </span>
      </span>
    </button>
  );
}

export default function ConceptPage(props: ConceptPageProps): JSX.Element {
  const { spaceId, spaceProductId, displayName } = props;
  const backToHome = `/space/${spaceId}/home`;
  const [searchParams] = useSearchParams();
  const [conceptOptions, setConceptOptions] = useState<ConceptBrief[]>([]);
  const [conceptCodes, setConceptCodes] = useState<string[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ConceptStockRow[]>([]);
  const [sortMode, setSortMode] = useState<ConceptSortMode>("dragon");
  const [sortOrder, setSortOrder] = useState<ConceptSortOrder>("desc");
  const [metricIndex, setMetricIndex] = useState(0);
  const [detailStockCode, setDetailStockCode] = useState<string | null>(null);
  const pendingAutoSearchRef = useRef(false);

  const metricColumn = CONCEPT_METRIC_COLUMNS[metricIndex] ?? CONCEPT_METRIC_COLUMNS[0];

  useEffect(() => {
    void (async () => {
      try {
        const list = await ConceptApi.listConcepts(spaceProductId);
        setConceptOptions(list);
      } catch {
        setConceptOptions([]);
      }
    })();
  }, [spaceProductId]);

  const runSearch = useCallback(
    async (codes: string[]) => {
      if (codes.length === 0) {
        setRows([]);
        return;
      }
      setLoading(true);
      try {
        const data = await ConceptApi.searchStocks({
          spaceProductId,
          conceptCodes: codes,
        });
        setRows(data);
        setSortMode("dragon");
        setSortOrder("desc");
      } catch (e) {
        message.error(e instanceof Error ? e.message : "查询失败");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [spaceProductId]
  );

  useEffect(() => {
    const code = searchParams.get("concept")?.trim();
    const auto = searchParams.get("search") === "1";
    if (!code) {
      return;
    }
    setConceptCodes([code]);
    if (auto) {
      pendingAutoSearchRef.current = true;
    }
  }, [searchParams]);

  useEffect(() => {
    if (!pendingAutoSearchRef.current || conceptCodes.length === 0) {
      return;
    }
    pendingAutoSearchRef.current = false;
    void runSearch(conceptCodes);
  }, [conceptCodes, runSearch]);

  const sortedRows = useMemo(
    () => sortConceptStockRows(rows, sortMode, sortOrder),
    [rows, sortMode, sortOrder]
  );

  const selectedBriefs = useMemo(() => {
    const out: ConceptBrief[] = [];
    for (const code of conceptCodes) {
      const hit = conceptOptions.find((c) => c.code === code);
      if (hit) {
        out.push(hit);
      }
    }
    return out;
  }, [conceptCodes, conceptOptions]);

  const handleSortToggle = useCallback(
    (field: ConceptSortField) => {
      if (sortMode === field) {
        setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
      } else {
        setSortMode(field);
        setSortOrder("desc");
      }
    },
    [sortMode]
  );

  const applyDragonSort = useCallback(() => {
    setSortMode("dragon");
    setSortOrder("desc");
  }, []);

  const cycleMetricColumn = useCallback(() => {
    setMetricIndex((prev) => (prev + 1) % CONCEPT_METRIC_COLUMNS.length);
  }, []);

  const openStockDetail = useCallback((stockCode: string) => {
    const code = stockCode.trim();
    if (!code) {
      return;
    }
    setDetailStockCode(code);
  }, []);

  const handleSearchConfirm = useCallback(
    (codes: string[]) => {
      setConceptCodes(codes);
      void runSearch(codes);
    },
    [runSearch]
  );

  const removeConceptCode = useCallback(
    (code: string) => {
      const next = conceptCodes.filter((c) => c !== code);
      setConceptCodes(next);
      if (next.length === 0) {
        setRows([]);
        return;
      }
      void runSearch(next);
    },
    [conceptCodes, runSearch]
  );

  const clearConceptCodes = useCallback(() => {
    setConceptCodes([]);
    setRows([]);
  }, []);

  const searchNavBtn = (
    <button
      type="button"
      className="lf-concept-page__nav-search"
      onClick={() => setSearchOpen(true)}
      aria-label="搜索概念"
    >
      <svg
        className="lf-concept-page__nav-search-icon"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <circle cx="10.5" cy="10.5" r="6.25" stroke="currentColor" strokeWidth="1.75" />
        <path
          d="M15.2 15.2L17.8 17.8"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );

  const { pullDistance, phase: pullPhase } = useMainScrollPullRefresh(
    async () => {
      if (conceptCodes.length === 0) {
        return;
      }
      await runSearch(conceptCodes);
    },
    true,
    CONCEPT_PAGE_SCROLL_SELECTOR
  );

  const ptrVisible = pullDistance > 0 || pullPhase === "refreshing";

  return (
    <div className="lf-concept-page-shell" style={conceptPageCssVars()}>
      <H5SubPageShell title={displayName} backTo={backToHome} headerRight={searchNavBtn}>
        <div className="lf-concept-page">
          <div className="lf-concept-page__fixed-top">
            <div
              className={
                ptrVisible ? "lf-concept-page__ptr lf-concept-page__ptr--visible" : "lf-concept-page__ptr"
              }
              style={{ height: ptrVisible ? `${pullDistance}px` : 0 }}
              aria-hidden={!ptrVisible}
            >
              <span className="lf-concept-page__ptr-text">
                {pullPhase === "refreshing" ? "刷新中…" : pullPhase === "ready" ? "松开刷新" : "下拉刷新"}
              </span>
            </div>

            <ConceptSelectedBar
              concepts={selectedBriefs}
              onRemove={removeConceptCode}
              onClear={clearConceptCodes}
            />

            <div className="lf-concept-page__thead">
              <button type="button" className="lf-concept-page__dragon-btn" onClick={applyDragonSort}>
                恢复潜龙排序
              </button>
              <div className="lf-concept-page__sort-cols">
                <SortHeader
                  label="最新价"
                  field="latestPrice"
                  active={sortMode === "latestPrice"}
                  order={sortOrder}
                  onToggle={handleSortToggle}
                />
                <SortHeader
                  label="涨跌幅"
                  field="pctChange"
                  active={sortMode === "pctChange"}
                  order={sortOrder}
                  onToggle={handleSortToggle}
                />
                <SortHeader
                  label={getMetricColumnHeaderLabel(metricColumn.field, metricColumn.label)}
                  field={metricColumn.field}
                  active={sortMode === metricColumn.field}
                  order={sortOrder}
                  onToggle={handleSortToggle}
                />
              </div>
            </div>
          </div>

          <div className="lf-concept-page__scroll">
            <Spin spinning={loading && pullPhase !== "refreshing"}>
              <div className="lf-concept-page__list">
              {sortedRows.length === 0 ? (
                conceptCodes.length === 0 ? (
                  <div className="lf-concept-page__empty-state">
                    <div className="lf-concept-page__pick-hint">先选择概念后查看其下股票</div>
                    <ConceptUsageGuide onOpenSearch={() => setSearchOpen(true)} />
                  </div>
                ) : (
                  <div className="lf-concept-page__empty">暂无数据</div>
                )
              ) : (
                sortedRows.map((row) => {
                  const tone = priceToneClass(row.pctChange);
                  return (
                    <div key={row.stockCode} className="lf-concept-page__row">
                      <button
                        type="button"
                        className="lf-concept-page__stock"
                        onClick={() => openStockDetail(row.stockCode)}
                      >
                        <span className="lf-concept-page__stock-name">{row.stockName}</span>
                        <span className="lf-concept-page__stock-code">
                          {formatConceptStockCodeDisplay(row.stockCode)}
                        </span>
                      </button>
                      <div className={`lf-concept-page__num lf-concept-page__col-price ${tone}`}>
                        {Number.isFinite(row.latestPrice) ? row.latestPrice.toFixed(2) : "—"}
                      </div>
                      <div className={`lf-concept-page__num lf-concept-page__col-pct ${tone}`}>
                        {formatPctDisplay(row.pctChange)}
                      </div>
                      <button
                        type="button"
                        className="lf-concept-page__num lf-concept-page__col-metric"
                        onClick={cycleMetricColumn}
                      >
                        {formatMetricValue(row, metricColumn.field)}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            </Spin>
          </div>
        </div>
      </H5SubPageShell>

      <ConceptSearchSheet
        open={searchOpen}
        spaceProductId={spaceProductId}
        catalog={conceptOptions}
        initialCodes={conceptCodes}
        onClose={() => setSearchOpen(false)}
        onConfirm={handleSearchConfirm}
      />

      <StockDetailModal
        open={detailStockCode != null}
        stockCode={detailStockCode ?? ""}
        onClose={() => setDetailStockCode(null)}
      />
    </div>
  );
}
